/**
 * Dynamic assessment domain types mirroring Reimagine.Domain DTOs and enums.
 * Enum request values use numeric literals (System.Text.Json default).
 * Enum response values from `.ToString()` use string literals.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const DynamicAssessmentConstants = {
  capabilityMaturityTypeCode: 'CAPABILITY_MATURITY',
  legacySubCapabilityDataTable: 'SubCapabilityData',
  fieldCodes: {
    currentRating: 'CURRENT_RATING',
    futureRating: 'FUTURE_RATING',
    currentDescription: 'CURRENT_DESCRIPTION',
    futureDescription: 'FUTURE_DESCRIPTION',
    businessValue: 'BUSINESS_VALUE',
    businessValueImpact: 'BUSINESS_VALUE_IMPACT',
  },
  sectionCodes: {
    currentState: 'CURRENT_STATE',
    futureState: 'FUTURE_STATE',
    diagnostic: 'DIAGNOSTIC',
    businessValue: 'BUSINESS_VALUE',
  },
} as const;

// ---------------------------------------------------------------------------
// Enums (numeric — used in request bodies)
// ---------------------------------------------------------------------------

export const AssessmentSubjectType = {
  SubCapability: 0,
  Project: 1,
  UseCase: 2,
} as const;
export type AssessmentSubjectType =
  (typeof AssessmentSubjectType)[keyof typeof AssessmentSubjectType];

export const AssessmentFieldType = {
  Rating: 0,
  TextArea: 1,
  Text: 2,
  Number: 3,
  SingleSelect: 4,
  MultiSelect: 5,
  Boolean: 6,
} as const;
export type AssessmentFieldType =
  (typeof AssessmentFieldType)[keyof typeof AssessmentFieldType];

export const AssessmentScopeType = {
  Global: 0,
  BusinessType: 1,
  Capability: 2,
  SubCapability: 3,
  Project: 4,
} as const;
export type AssessmentScopeType =
  (typeof AssessmentScopeType)[keyof typeof AssessmentScopeType];

export const AssessmentInstanceStatus = {
  Draft: 0,
  InProgress: 1,
  Submitted: 2,
  Locked: 3,
} as const;
export type AssessmentInstanceStatus =
  (typeof AssessmentInstanceStatus)[keyof typeof AssessmentInstanceStatus];

export const AssessmentOptionSetType = {
  Ordinal: 0,
  Numeric: 1,
  Labeled: 2,
} as const;
export type AssessmentOptionSetType =
  (typeof AssessmentOptionSetType)[keyof typeof AssessmentOptionSetType];

export const AssessmentOptionDisplayMode = {
  Slider: 0,
  Radio: 1,
  Dropdown: 2,
} as const;
export type AssessmentOptionDisplayMode =
  (typeof AssessmentOptionDisplayMode)[keyof typeof AssessmentOptionDisplayMode];

export const AssessmentTemplateVersionStatus = {
  Draft: 0,
  Published: 1,
  Archived: 2,
} as const;
export type AssessmentTemplateVersionStatus =
  (typeof AssessmentTemplateVersionStatus)[keyof typeof AssessmentTemplateVersionStatus];

export const AssessmentTemplateScopeLevel = {
  System: 0,
  Organization: 1,
  User: 2,
} as const;
export type AssessmentTemplateScopeLevel =
  (typeof AssessmentTemplateScopeLevel)[keyof typeof AssessmentTemplateScopeLevel];

export const AssessmentLegacyTransformRule = {
  Direct: 0,
  OptionToInt: 1,
  Text: 2,
} as const;
export type AssessmentLegacyTransformRule =
  (typeof AssessmentLegacyTransformRule)[keyof typeof AssessmentLegacyTransformRule];

// String names returned by runtime form-schema and summary endpoints.
export type AssessmentFieldTypeName =
  | 'Rating'
  | 'TextArea'
  | 'Text'
  | 'Number'
  | 'SingleSelect'
  | 'MultiSelect'
  | 'Boolean';

export type AssessmentInstanceStatusName =
  | 'Draft'
  | 'InProgress'
  | 'Submitted'
  | 'Locked';

export type AssessmentScopeTypeName =
  | 'Global'
  | 'BusinessType'
  | 'Capability'
  | 'SubCapability'
  | 'Project';

// ---------------------------------------------------------------------------
// Runtime — form schema
// ---------------------------------------------------------------------------

export interface GetAssessmentFormSchemaRequest {
  userId: number;
  projectId: number;
  subjectType: AssessmentSubjectType;
  subjectId: number;
  assessmentTypeCode?: string;
}

export interface AssessmentFormSchema {
  templateVersionId: number;
  templateId: number;
  templateName: string;
  versionNumber: number;
  assessmentTypeCode: string;
  sections: AssessmentFormSection[];
}

export interface AssessmentFormSection {
  code: string;
  title: string;
  description?: string;
  displayOrder: number;
  isCollapsible: boolean;
  fields: AssessmentFormField[];
}

export interface AssessmentFormField {
  fieldCode: string;
  fieldType: AssessmentFieldTypeName;
  label: string;
  placeholder?: string;
  helpText?: string;
  isRequired: boolean;
  displayOrder: number;
  defaultValue?: string;
  optionSet?: AssessmentFormOptionSet;
}

export interface AssessmentFormOptionSet {
  name: string;
  optionSetType: string;
  displayMode: string;
  options: AssessmentFormOption[];
}

export interface AssessmentFormOption {
  optionValue: number;
  label: string;
  definition?: string;
  displayOrder: number;
  isDefault: boolean;
}

// ---------------------------------------------------------------------------
// Runtime — responses
// ---------------------------------------------------------------------------

export interface GetDynamicAssessmentResponsesRequest {
  userId: number;
  projectId: number;
  subjectType: AssessmentSubjectType;
  subjectId: number;
  assessmentTypeCode?: string;
}

export interface DynamicAssessmentResponse {
  instanceId?: number;
  templateVersionId?: number;
  status: AssessmentInstanceStatusName | string;
  isDraft: boolean;
  responses: DynamicAssessmentResponseItem[];
}

export interface DynamicAssessmentResponseItem {
  fieldCode: string;
  textValue?: string;
  numericValue?: number;
  booleanValue?: boolean;
  optionValue?: number;
  optionLabel?: string;
  jsonValue?: string;
}

export interface SaveDynamicAssessmentResponsesRequest {
  instanceId?: number;
  templateVersionId: number;
  userId: number;
  projectId: number;
  subjectType: AssessmentSubjectType;
  subjectId: number;
  assessmentTypeCode?: string;
  isDraft: boolean;
  responses: SaveDynamicAssessmentResponseItem[];
}

export interface SaveDynamicAssessmentResponseItem {
  fieldCode: string;
  textValue?: string;
  numericValue?: number;
  booleanValue?: boolean;
  optionValue?: number;
  jsonValue?: string;
}

// ---------------------------------------------------------------------------
// Admin — templates
// ---------------------------------------------------------------------------

export interface AssessmentTemplateSummary {
  id: number;
  name: string;
  description?: string;
  assessmentTypeCode: string;
  isSystemDefault: boolean;
  isActive: boolean;
  scopeLevel: string;
  latestVersionNumber?: number;
  latestVersionStatus?: string;
  latestVersionId?: number;
}

export interface AssessmentTemplateVersionDetail {
  id: number;
  templateId: number;
  templateName: string;
  versionNumber: number;
  status: string;
  changeNotes?: string;
  sections: AssessmentTemplateSectionDetail[];
  optionSets: AssessmentTemplateOptionSetDetail[];
}

export interface AssessmentTemplateSectionDetail {
  id?: number;
  code: string;
  title: string;
  description?: string;
  displayOrder: number;
  isCollapsible: boolean;
  isActive?: boolean;
  fields: AssessmentTemplateFieldDetail[];
}

export interface AssessmentTemplateFieldDetail {
  id?: number;
  fieldCode: string;
  fieldType: AssessmentFieldType;
  label: string;
  placeholder?: string;
  helpText?: string;
  isRequired: boolean;
  displayOrder: number;
  defaultValue?: string;
  validationRules?: string;
  optionSetIndex?: number;
  isActive?: boolean;
  legacyMap?: AssessmentLegacyFieldMap;
}

export interface AssessmentLegacyFieldMap {
  legacyTable: string;
  legacyColumn: string;
  transformRule: AssessmentLegacyTransformRule;
}

export interface AssessmentTemplateOptionSetDetail {
  id?: number;
  name: string;
  optionSetType: AssessmentOptionSetType;
  minValue?: number;
  maxValue?: number;
  allowHalfSteps: boolean;
  displayMode: AssessmentOptionDisplayMode;
  options: AssessmentTemplateOptionDetail[];
}

export interface AssessmentTemplateOptionDetail {
  id?: number;
  optionValue: number;
  label: string;
  definition?: string;
  displayOrder: number;
  isDefault: boolean;
  isActive?: boolean;
}

export interface CloneAssessmentTemplateRequest {
  sourceTemplateId: number;
  name: string;
  description?: string;
  ownerUserId?: number;
}

export interface SaveAssessmentTemplateVersionRequest {
  versionId?: number;
  templateId: number;
  changeNotes?: string;
  sections: AssessmentTemplateSectionDetail[];
  optionSets: AssessmentTemplateOptionSetDetail[];
}

export interface CreateAssessmentTemplateAssignmentRequest {
  assessmentTemplateVersionId: number;
  scopeType: AssessmentScopeType;
  scopeId?: number;
  priority?: number;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface AssessmentTemplateAssignment {
  id: number;
  assessmentTemplateVersionId: number;
  templateName: string;
  versionNumber: number;
  scopeType: AssessmentScopeTypeName | string;
  scopeId?: number;
  priority: number;
  isActive: boolean;
}

export interface RebuildTemplateResolutionsRequest {
  assessmentTypeCode?: string;
}

export interface RebuildTemplateResolutionsResult {
  rebuiltCount: number;
  assessmentTypeCode: string;
}

export interface AssessmentTemplateFieldUsage {
  fieldCode: string;
  responseCount: number;
}

export interface AssessmentTemplateOptionUsage {
  optionValue: number;
  label?: string;
  responseCount: number;
}

export interface AssessmentTemplateVersionUsage {
  versionId: number;
  responseInstanceCount: number;
  usedFields: AssessmentTemplateFieldUsage[];
  usedOptions: AssessmentTemplateOptionUsage[];
}

export interface RestoreAssessmentTemplateVersionDefaultsRequest {
  changeNotes?: string;
}

// ---------------------------------------------------------------------------
// Hook / UI state helpers (no rendering)
// ---------------------------------------------------------------------------

export interface DynamicAssessmentSubject {
  userId: number;
  projectId: number;
  subjectType: AssessmentSubjectType;
  subjectId: number;
  assessmentTypeCode?: string;
}

export interface DynamicAssessmentFormState {
  schema?: AssessmentFormSchema;
  responses: DynamicAssessmentResponse;
  loading: boolean;
  saving: boolean;
  error?: string;
}

export interface AssessmentTemplateListState {
  templates: AssessmentTemplateSummary[];
  loading: boolean;
  error?: string;
}

export interface AssessmentTemplateVersionState {
  version?: AssessmentTemplateVersionDetail;
  loading: boolean;
  error?: string;
}
